import { Component, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import {MatLegacyPaginator as MatPaginator} from '@angular/material/legacy-paginator';
import { EnvService } from '../services/env.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { debounceTime } from 'rxjs';
import { CmdbMountPointsFilter } from './cmdb-mount-points.filter';
import { SettingsPersistenceService } from '../services/settings-persistence.service';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'micro-cmdb-mount-points',
  templateUrl: './cmdb-mount-points.component.html',
})
export class CmdbMountPointsComponent {
  dataSource = new MatTableDataSource<any>();
  osTypeNames = new Map<string, string>();
  statusNames = new Map<string, string>();
  typeNames = new Map<string, string>();
  fileSystemNames = new Map<string, string>();

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  pageSettings!: SettingsPersistenceService;
  filter = new CmdbMountPointsFilter(this.auth);

  constructor(public auth: AuthService, private env: EnvService, private http: HttpClient) {
  }

  ngOnInit(): void {
    // Setup the settings persistence service for this page
    this.pageSettings = new SettingsPersistenceService(sessionStorage, 'micro.cmdbMountPoints');
    this.pageSettings
      .setFilter(this.filter)
      .setPaginator(this.paginator)
      .init();

    // Subscribe to filter changes with a debounce time to avoid excessive reloads
    this.filter.anyChange.pipe(debounceTime(500)).subscribe(change => {
      this.reload();
    });

    // Initial data load
    this.reload();
  }

  ngOnDestroy(): void {
  }

  reload(): void {
    const params: HttpParams = this.filter.getParams()
      .set('page', String(this.paginator.pageIndex || 0))
      .set('size', String(this.paginator.pageSize || 20));

    this.http.get(`${this.env.e.url}/domain/CmdbMountPoints`, {params}).subscribe(data => {
      const page = data as any;
      this.dataSource.data = page.content;
      this.paginator.pageIndex = page.number;
      this.paginator.pageSize = page.size;
      this.paginator.length = page.totalElements;
      this.loadOsTypeNames(page.content || []);
      this.loadStatusNames(page.content || []);
      this.loadTypeNames(page.content || []);
      this.loadFileSystemNames(page.content || []);
    });
  }

  private loadOsTypeNames(mountPoints: any[]): void {
    const osTypeIds = [...new Set(mountPoints
      .map(mp => mp.osTypeId)
      .filter(id => id && !this.osTypeNames.has(id)))] as string[];

    osTypeIds.forEach(id => {
      this.http.get(`${this.env.e.url}/domain/cmdbOsType/${id}`).subscribe({
        next: (osType: any) => {
          this.osTypeNames.set(id, osType ? osType.name : '');
        },
        error: () => {
          this.osTypeNames.set(id, '');
        }
      });
    });
  }

  private loadStatusNames(mountPoints: any[]): void {
    const statusIds = [...new Set(mountPoints
      .map(mp => mp.statusId)
      .filter(id => id && !this.statusNames.has(id)))] as string[];

    statusIds.forEach(id => {
      this.http.get(`${this.env.e.url}/domain/cmdbMountStatus/${id}`).subscribe({
        next: (status: any) => {
          this.statusNames.set(id, status ? status.name : '');
        },
        error: () => {
          this.statusNames.set(id, '');
        }
      });
    });
  }

  private loadTypeNames(mountPoints: any[]): void {
    const typeIds = [...new Set(mountPoints
      .map(mp => mp.typeId)
      .filter(id => id && !this.typeNames.has(id)))] as string[];

    typeIds.forEach(id => {
      this.http.get(`${this.env.e.url}/domain/cmdbMountTypes/${id}`).subscribe({
        next: (type: any) => {
          this.typeNames.set(id, type ? type.name : '');
        },
        error: () => {
          this.typeNames.set(id, '');
        }
      });
    });
  }

  private loadFileSystemNames(mountPoints: any[]): void {
    const fileSystemIds = [...new Set(mountPoints
      .map(mp => mp.fileSystemId)
      .filter(id => id && !this.fileSystemNames.has(id)))] as string[];

    fileSystemIds.forEach(id => {
      this.http.get(`${this.env.e.url}/domain/CmdbFileSystems/${id}`).subscribe({
        next: (fileSystem: any) => {
          this.fileSystemNames.set(id, fileSystem ? fileSystem.name : '');
        },
        error: () => {
          this.fileSystemNames.set(id, '');
        }
      });
    });
  }
}
